import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { renderBrandLogoSheetBlob, type LogoSheetLogo } from './renderLogoSheet';
import type { Brand } from '../../components/generation/hooks/useBrandLogos';

/**
 * 1 ブランドぶんのロゴ1枚シートを Canvas で描画し、可能ならサーバー保存する。
 *
 * 「ロゴ1枚シート」(生成 API に渡すのと同じ canonical なシート) は Canvas 描画のため
 * ブラウザでしか作れない。未生成ブランドはバックエンドが生成時に作れず個別ロゴへ
 * フォールバックしてしまうので、生成/ダウンロード前にこの場で描画してサーバー保存
 * (バックフィル) する。
 *
 * @returns 保存できた場合は canonical な signed URL、描画した blob (保存可否によらず)。
 */
export async function renderAndSaveBrandSheet(
  brand: Brand,
): Promise<{ savedUrl: string | null; blob: Blob | null }> {
  const logos: LogoSheetLogo[] = (brand.logos ?? []).map((l) => ({
    background: l.background,
    color_type: l.color_type,
    layout: l.layout,
    description: l.description,
    logos: { url: l.logos?.url },
  }));
  if (logos.length === 0) return { savedUrl: null, blob: null };
  const result = await renderBrandLogoSheetBlob(logos, { brandName: brand.brand_name });
  if (!result) return { savedUrl: null, blob: null };

  // サーバー保存 (楽観ロック付き)。成功すれば canonical な signed URL が返る。
  let savedUrl: string | null = null;
  if (brand.logo_source_token) {
    try {
      const form = new FormData();
      form.append('file', new File([result.blob], `${brand.id}.png`, { type: 'image/png' }));
      form.append('source_token', brand.logo_source_token);
      const { data } = await apiClient.post<{ status: string; url?: string }>(
        `/brands/${brand.id}/logo-sheet`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      if (data?.status === 'ok') savedUrl = data.url ?? null;
    } catch {
      // 保存失敗時は blob のみ返す (呼び出し側でフォールバック)
    }
  }
  return { savedUrl, blob: result.blob };
}

/**
 * 未生成シートを持つブランドを生成前に一括バックフィルする。生成・ダウンロード両方から呼ぶ。
 * 1 つでも保存できたら brand-logos クエリを無効化して次回以降を高速パスにする。
 */
export async function backfillMissingLogoSheets(
  brands: Brand[],
  queryClient: QueryClient,
): Promise<void> {
  const pending = brands.filter(
    (b) => !b.logo_sheet_url && b.logos && b.logos.length > 0,
  );
  if (pending.length === 0) return;
  const results = await Promise.all(pending.map(renderAndSaveBrandSheet));
  if (results.some((r) => r.savedUrl)) {
    queryClient.invalidateQueries({ queryKey: ['brand-logos'] });
  }
}
